import { locationCoordsRegex } from '@/services/location'
import { type CsvRowData } from '../utils'
import { registerDeviceTemplateFields, registerDeviceValidationErrors } from './utils'

type ValidationResult = boolean | string

interface ValidationMessage {
  result: (result: ValidationResult) => ValidationResult
}

interface ValidationData {
  dataValidation: (message: ValidationMessage) => ValidationResult
}

export const readCsvContent = async (csvFile: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsText(csvFile, 'UTF-8')
    reader.onload = (evt: ProgressEvent<FileReader>) => {
      resolve(evt.target?.result as string)
    }
    reader.onerror = reject
  })
}

export const validateCsvFile = async (csvContent: string): Promise<string> => {
  if (csvContent.length === 0) {
    return registerDeviceValidationErrors.blankFile
  } else {
    const lines = getCsvLines(csvContent)

    if (lines.length <= 1) {
      return registerDeviceValidationErrors.blankFile
    } else {
      const headers = getCsvHeaders(lines)
      if (JSON.stringify(headers) !== JSON.stringify(Object.values(registerDeviceTemplateFields))) {
        return registerDeviceValidationErrors.columnHeaders
      }
    }
    return ''
  }
}

export const getCsvData = async (csvContent: string): Promise<CsvRowData[]> => {
  const jsonData: CsvRowData[] = []
  const lines = getCsvLines(csvContent)
  const headers = getCsvHeaders(lines)

  for (let i = 1; i < lines.length; i++) {
    const jsonRow = {} as CsvRowData
    const currentLine = lines[i].split(',')
    if (currentLine.every((cl) => cl === '')) {
      continue
    }
    for (let j = 0; j < headers.length; j++) {
      ;(jsonRow as unknown as Record<string, string | number>)[headers[j]] =
        currentLine[j]?.trim() ?? ''
    }
    jsonRow.RowId = i + 1
    jsonData.push(jsonRow)
  }
  return jsonData
}

const getCsvLines = (csvContent: string): string[] => {
  return csvContent
    .replaceAll(';', ',')
    .split('\r\n')
    .filter((line) => line !== '')
}

const getCsvHeaders = (lines: string[]): string[] => {
  return lines[0].split(',')
}

const createValidation = (validation: (data: string) => boolean, errorMessage: string) => {
  return (data: string, row: number): ValidationData => {
    const validationResult = validation(data)
    return {
      dataValidation: (message: ValidationMessage): ValidationResult =>
        validationResult
          ? message.result(validationResult)
          : `Row ${row} - ${message.result(errorMessage)}`
    }
  }
}

const getValidationResults = (
  values: Record<string, ValidationData>
): { result: ValidationResult }[] => {
  return Object.keys(values)
    .map((key) => ({
      result: values[key].dataValidation({
        result: (r: ValidationResult) => r
      })
    }))
    .filter((item) => item.result !== true)
}

export const validateCsvRow = (csvData: CsvRowData): string[] => {
  const csvValues = {
    deviceName: createDeviceNameValidation(
      csvData[registerDeviceTemplateFields.deviceName],
      csvData.RowId
    ),
    locationName: createLocationNameValidation(
      csvData[registerDeviceTemplateFields.locationName],
      csvData.RowId
    ),
    deviceSerial: createDeviceSerialValidation(
      csvData[registerDeviceTemplateFields.deviceSerialNumber],
      csvData.RowId
    ),
    indoorSensor: createIndoorSensorValidation(
      (csvData[registerDeviceTemplateFields.indoorSensor] ?? '').toLowerCase(),
      csvData.RowId
    ),
    latitude: createLatitudeValidation(
      csvData[registerDeviceTemplateFields.latitude],
      csvData.RowId
    ),
    longitude: createLongitudeValidation(
      csvData[registerDeviceTemplateFields.longitude],
      csvData.RowId
    ),
    nullIsland: createNullIslandValidation(
      `${csvData[registerDeviceTemplateFields.longitude]},${csvData[registerDeviceTemplateFields.latitude]}`,
      csvData.RowId
    )
  }
  return getValidationResults(csvValues).map((error) => error.result as string)
}

const createDeviceNameValidation = createValidation(
  (data) => data && data.length <= 255,
  registerDeviceValidationErrors.deviceName
)

const createLocationNameValidation = createValidation(
  (data) => data && data.length <= 255,
  registerDeviceValidationErrors.locationName
)

const createDeviceSerialValidation = createValidation(
  (data) =>
    data &&
    (((data.startsWith('8143') || data.startsWith('8144')) && data.length === 11) ||
      (data.startsWith('8145') && data.length === 13)),
  registerDeviceValidationErrors.deviceSerialNumber
)

const createIndoorSensorValidation = createValidation(
  (data) => (data && data === 'yes') || data === 'no' || (data && data === 'y') || data === 'n',
  registerDeviceValidationErrors.indoorSensor
)

const createLatitudeValidation = createValidation(
  (data) => data && locationCoordsRegex.latitude.test(data),
  registerDeviceValidationErrors.latitude
)

const createLongitudeValidation = createValidation(
  (data) => data && locationCoordsRegex.longitude.test(data),
  registerDeviceValidationErrors.longitude
)

const createNullIslandValidation = createValidation(
  (data) => data && data !== '0,0',
  registerDeviceValidationErrors.nullIsland
)
